import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CollapsibleCard } from '@/components/pnr/collapsible-card';
import { PnrPaxRow } from '@/components/pnr/pnr-pax-row';
import { RevealableContact } from '@/components/pnr/revealable-contact';
import { Badge } from '@/components/ui/badge';
import { PnrNotificationCell } from '@/components/pnr/pnr-notification-cell';
import { useCaseNotifications } from '@/lib/notifications/use-notifications';
import type { DisruptionPaxRosterRow } from '@/lib/disruptions/types';

// The PNR's co-travellers. Rows are separated by a horizontal divider (no per-pax
// card box). The pax-type breakdown lives in the hero; the title count is enough
// here. Inside the operation center this card also carries the per-PNR offer
// notification + acceptance gate (N11): the group's delivery/acceptance status +
// the resend (channel-select) menu + the supervisor force action, plus each pax
// row's acceptance chip — so the operator can work the gate from the open PNR.

export function PassengersCard({
  passengers,
  caseUrn,
  disruptionUrn,
  defaultOpen = false,
}: {
  passengers: DisruptionPaxRosterRow[];
  caseUrn: string;
  /** When known, each row offers the desk's condition / responsible-adult writes (#806). */
  disruptionUrn?: string | undefined;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation('pnr');
  // All passengers in this card belong to ONE PNR group (one sub-case); the offer
  // is accepted at the group level, so a single notification row backs the card.
  const subCaseUrn = passengers[0]?.subCaseUrn;
  const groupLabel = passengers[0]?.lastName ?? t('group.thisPnr');
  const { rowFor } = useCaseNotifications(caseUrn, { enabled: !!subCaseUrn });
  const notification = subCaseUrn ? rowFor(subCaseUrn) : undefined;

  return (
    <CollapsibleCard
      title={t('passengers.title', { n: passengers.length })}
      icon={<Users className="h-4 w-4" aria-hidden />}
      defaultOpen={defaultOpen}
    >
      {subCaseUrn ? (
        <div className="mb-2 border-b border-border pb-3" data-testid="drawer-notification-gate">
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('passengers.offerNotification')}
          </p>
          <PnrNotificationCell caseUrn={caseUrn} subCaseUrn={subCaseUrn} groupLabel={groupLabel} />
        </div>
      ) : null}
      {/* passenger-profile-completion: the party's completion stamp + the contact on
          file (presence only; the value is one audited click away). A party with
          nothing on file is exactly the one the counter QR exists for. */}
      {passengers[0] ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-border pb-3" data-testid="drawer-profile-facts">
          {passengers[0].profile ? (
            <Badge
              variant={passengers[0].profile.source === 'PASSENGER_SELF_SERVICE' ? 'success' : 'neutral'}
              size="sm"
              title={t('profile.completedTitle', { date: new Date(passengers[0].profile.completedAt).toLocaleString() })}
              data-testid="drawer-profile-completed"
            >
              {t(`profile.completedBy.${passengers[0].profile.source}`, {
                time: new Date(passengers[0].profile.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              })}
            </Badge>
          ) : null}
          <RevealableContact caseUrn={caseUrn} paxUrn={passengers[0].paxUrn} contactOnFile={passengers[0].contactOnFile} />
        </div>
      ) : null}
      <div className="divide-y divide-border" data-testid="drawer-passengers">
        {passengers.map((p) => (
          <PnrPaxRow
            key={p.paxUrn}
            row={p}
            caseUrn={caseUrn}
            disruptionUrn={disruptionUrn}
            notification={rowFor(p.subCaseUrn) ?? notification}
          />
        ))}
      </div>
    </CollapsibleCard>
  );
}
